import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { createCheckout } from '../utils/api';

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const tiers = [
    {
      name: 'Free',
      price: '$0',
      tier: 'free',
      scans: '1 scan lifetime',
      features: [
        '1 contract analysis',
        'AI-powered risk assessment',
        'Basic issue detection',
        'PDF & DOC reports',
        'Email support'
      ],
      cta: 'Current Plan',
      disabled: true
    },
    {
      name: 'Pay Per Scan',
      price: '$2.49',
      tier: 'pay_per_scan',
      priceId: process.env.REACT_APP_STRIPE_PRICE_ID_PAY_PER_SCAN,
      scans: 'per analysis',
      features: [
        'Single contract analysis',
        'Full AI analysis',
        'Detailed risk scores',
        'PDF & DOC reports',
        'Priority support',
        'No commitment'
      ],
      cta: 'Buy Now',
      popular: false
    },
    {
      name: 'Monthly',
      price: '$14.99',
      tier: 'monthly',
      priceId: process.env.REACT_APP_STRIPE_PRICE_ID_MONTHLY,
      scans: '20 scans/month',
      features: [
        '20 contract analyses per month',
        '20 AI questions per month',
        'Advanced AI analysis',
        'PDF & DOC reports',
        'Unlimited history access',
        'Priority email support',
        'Cancel anytime'
      ],
      cta: 'Subscribe',
      popular: true
    },
    {
      name: 'Pro',
      price: '$39.99',
      tier: 'pro',
      priceId: process.env.REACT_APP_STRIPE_PRICE_ID_PRO,
      scans: '80 scans/month',
      features: [
        '80 contract analyses per month',
        '60 AI questions per month',
        'Premium AI analysis',
        'PDF & DOC reports',
        'API access',
        'Dedicated support'
      ],
      cta: 'Subscribe',
      popular: false
    }
  ];

  const handlePurchase = async (tier, priceId) => {
    if (!user) {
      navigate('/signup');
      return;
    }

    if (!priceId) return;

    setLoading(tier);
    try {
      const response = await createCheckout(priceId, tier);
      window.location.href = response.data.checkout_url;
    } catch (err) {
      alert('Failed to create checkout session');
      setLoading(null);
    }
  };

  return (
    <div className="bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Simple, Transparent Pricing</h1>
          <p className="text-xl text-gray-600">
            Choose the plan that fits your needs. Upgrade or downgrade anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`bg-white rounded-lg shadow-lg overflow-hidden ${
                tier.popular ? 'ring-2 ring-primary-600' : ''
              }`}
            >
              {tier.popular && (
                <div className="bg-primary-600 text-white text-center py-2 text-sm font-semibold">
                  Most Popular
                </div>
              )}

              <div className="p-6">
                <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold">{tier.price}</span>
                  {tier.tier !== 'free' && (
                    <span className="text-gray-600">/{tier.tier === 'pay_per_scan' ? 'scan' : 'month'}</span>
                  )}
                </div>
                <p className="text-gray-600 mb-6">{tier.scans}</p>

                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <svg
                        className="w-5 h-5 text-green-500 mr-2 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePurchase(tier.tier, tier.priceId)}
                  disabled={tier.disabled || loading === tier.tier || (user?.tier === tier.tier)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                    tier.popular
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading === tier.tier
                    ? 'Loading...'
                    : user?.tier === tier.tier
                    ? 'Current Plan'
                    : tier.cta}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow p-8">
          <h2 className="text-2xl font-bold mb-4">Need a custom solution?</h2>
          <p className="text-gray-600 mb-4">
            For enterprise needs with unlimited scans, custom integrations, and dedicated support,
            contact our sales team.
          </p>
          <a
            href="mailto:admin@contractguard.com.au"
            className="inline-block bg-primary-600 text-white hover:bg-primary-700 px-6 py-3 rounded-lg font-semibold"
          >
            Contact Sales
          </a>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
